import { useQueryClient } from "@tanstack/react-query";
import { Skeleton } from "antd";
import { useEffect, useState } from "react";
import { UseFormReturn } from "react-hook-form";

import { useTranslation } from "../../core/translation.core";
import useCart from "../../hooks/services/useCart.hook";
import useProductQuery from "../../hooks/services/useProductQuery";
import ErrorCenterIcon from "../../icons/exclamationTriangleCenter.icon";
import { Product, ProductListFilter } from "../../types/product";
import AttachedProduct from "../newOrders/AttachProduct.comp";
import VariableProduct from "../newOrders/VariableProduct.comp";
import CustomProductModal from "../newOrders/customProductModal.comp";
import { formatCurrency } from "../../core/currency.core";

export default function PosProductCard({ methods }: { methods: UseFormReturn<ProductListFilter, any> }) {
  const { msg, t } = useTranslation();
  const { data: products, isFetched, isError, failureReason } = useProductQuery(methods);

  if (isError) return <ErrorCenterIcon reason={failureReason} />;
  if (!isFetched) return <Skeleton />;

  if (!products?.list?.length)
    return (
      <h1 id="no-product-found" className="dark:text-white text-2xl">
        {t(msg.product.noProductsFound)}
      </h1>
    );

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 gap-4 auto-rows-fr" id="pos-product-list">
      {products?.list?.map((p) => (
        <ProductItem key={p.id} product={p} />
      ))}
    </div>
  );
}

function ProductItem({ product }: { product: Product }) {
  const { addItem, addItemData, addItemSuccess, updateItem, getProductWarehouseId, cart } = useCart();
  const [showAttachProduct, setShowAttachProduct] = useState(false);
  const [showVariableProduct, setShowVariableProduct] = useState(false);
  const [showCustomProduct, setShowCustomProduct] = useState(false);
  const { msg, t } = useTranslation();

  const queryClient = useQueryClient();

  async function addCart(product: Product) {
    const quantity = 1;

    const inCart = cart?.items?.find((item: any) => item.product.id === product.id);
    if (!inCart) {
      const warehouseId = getProductWarehouseId(product);
      //  @ts-ignore
      addItem({ product, quantity, warehouseId });
      queryClient.invalidateQueries({ queryKey: ["PosCartCount"] });
    } else {
      const updatedQuantity = inCart.quantity + quantity;
      updateItem({ product, quantity: updatedQuantity, warehouseId: inCart.warehouseId, cartItemId: inCart.id });
    }
  }

  const handleClick = () => {
    if (product.productType === "variationParent") {
      setShowVariableProduct(true);
    } else if (product.productType === "custom") {
      setShowCustomProduct(true);
    } else {
      addCart(product);
    }
  };

  useEffect(() => {
    if (product?.attachProducts?.find((p) => p.placement === "pos" && p.suggestionType === "auto" && addItemSuccess)) {
      setShowAttachProduct(true);
    }
  }, [addItemSuccess, product?.attachProducts]);

  return (
    <section id="pos-product" className="grow-1">
      <button
        attr-name={product?.name}
        className="px-5 h-full w-full space-y-4 rounded-lg bg-white dark:bg-her-darkblue2 dark:shadow-md dark:text-white shadow-sm text-center py-5"
        onClick={handleClick}
      >
        <h1 className="font-bold text-xs break-words">{product?.name}</h1>
        <h3 className="text-xs">SKU: {product.sku}</h3>
        <h4 className="font-bold text-base text-her-green" attr-name="product-price">
          {getProductPrice(product)}
        </h4>
        <p className="text-xs mt-1">
          {t(msg.product.inStock)}:
          {product.retailInventories?.find((inventory) => inventory.warehouseId === getProductWarehouseId(product))?.count || 0}
        </p>
      </button>

      {showAttachProduct && (
        <AttachedProduct
          parentProduct={product}
          setShowAttachProduct={setShowAttachProduct}
          attachProducts={product.attachProducts}
          //  @ts-ignore
          attachCartItemId={addItemData.cartItemId}
        />
      )}

      {showVariableProduct && (
        //  @ts-ignore
        <VariableProduct
          parentProduct={product}
          setShowVariableProduct={setShowVariableProduct}
          addItem={addItem}
          updateItem={updateItem}
          getProductWarehouseId={getProductWarehouseId}
          cart={cart}
        />
      )}

      {showCustomProduct && <CustomProductModal customProduct={product} setShowCustomProduct={setShowCustomProduct} />}
    </section>
  );
}

const getProductPrice = (product: Product) => {
  if (!product) return;

  if (["variationParent"].includes(product.productType)) {
    const priceArr = product.variations?.map((v) => v.price) ?? [];

    if (priceArr.length < 1) return formatCurrency(product.price, product.currency);

    const minPrice = formatCurrency(Math.min(...priceArr), product.currency);
    const maxPrice = formatCurrency(Math.max(...priceArr), product.currency);

    if (minPrice === maxPrice) return maxPrice;
    return `${minPrice} - ${maxPrice}`;
  }

  return formatCurrency(product.price, product.currency);
};
